import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import {
  entityConfig,
  setEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { firstValueFrom } from 'rxjs';
import { ResourceStatusService } from '@thiememeulenhoff/ng-api-gateway';
import {
  ResourceStatus,
  ResourceStatusCollection,
} from '@thiememeulenhoff/types';
import { withRequestStatus } from '../_utils/request-status.feature';
import {
  buildResourceStatusIdentifier,
  convertToResourceStatuses,
} from './resource-status.facade.util';

const resourceStatusEntityConfig = entityConfig({
  entity: {} as ResourceStatus,
  selectId: (e: ResourceStatus) => e.identifier,
});

export const ResourceStatusStore = signalStore(
  { providedIn: 'root' },
  withEntities(resourceStatusEntityConfig),
  withRequestStatus(),
  withComputed(({ entities }) => ({
    resourceStatuses: computed(() => entities()),
  })),
  withMethods(
    (store, resourceStatusService = inject(ResourceStatusService)) => ({
      disabledStatus: (actorId: string, contentPath: string) =>
        computed(() =>
          store
            .entities()
            .find(
              (e) =>
                e.identifier ===
                buildResourceStatusIdentifier(actorId, contentPath, 'disabled'),
            ),
        ),
      reviewStatus: (actorId: string, contentPath: string) =>
        computed(() =>
          store
            .entities()
            .find(
              (e) =>
                e.identifier ===
                buildResourceStatusIdentifier(
                  actorId,
                  contentPath,
                  'reviewDisabled',
                ),
            ),
        ),
      resourceStatusesForAction: (action: string) =>
        computed(() => store.entities().filter((e) => e.action === action)),
      resourceStatusesForActor: (actor: string) =>
        computed(() => store.entities().filter((e) => e.actor === actor)),
      async fetchGroupResourceStatus(
        groupCode: string,
        arrangementCode: string,
        contentIdentifier: string,
      ): Promise<void> {
        patchState(store, { requestStatus: 'loading' });
        try {
          const states = await firstValueFrom(
            resourceStatusService.fetchGroupResourceStatus(
              groupCode,
              arrangementCode,
              contentIdentifier,
            ),
          );
          patchState(
            store,
            setEntities(
              convertToResourceStatuses(states),
              resourceStatusEntityConfig,
            ),
            { requestStatus: 'success' },
          );
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to fetch';
          patchState(store, { requestStatus: { error: message } });
          throw error;
        }
      },
      async storeResourceStatuses(
        arrangementCode: string,
        collection: ResourceStatusCollection,
      ): Promise<void> {
        await firstValueFrom(
          resourceStatusService.storeResourceStatuses(
            arrangementCode,
            collection,
          ),
        );
      },
    }),
  ),
);
